/**
 * Páginas do blog do site: listagem (/blog) e artigo (/blog/:slug)
 *
 * Mesmo visual do site institucional. As duas páginas são pré-renderizadas
 * no build (scripts/prerender.mjs) para buscadores e robôs de IA.
 *
 * @author Equipe VendeAi
 * @since 1.4.0
 */

import { ReactNode, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CalendarDays, Clock, Lightbulb } from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { BRAND } from '../content';
import { findSegmentBySlug } from '../segments';
import { usePageMeta } from '../usePageMeta';
import { usePlatformData } from '../usePlatformData';
import { useHashScroll } from '../useHashScroll';
import { SiteHeader } from '../components/SiteHeader';
import { SiteFooter, WhatsAppFloat, MobileStickyCta, Faq } from '../components/FaqContact';
import { RichText } from './RichText';
import {
  BLOG_ARTICLES,
  BLOG_CATEGORIES,
  BLOG_DESCRIPTION,
  BLOG_TITLE,
  findArticle,
  relatedArticles,
} from './index';
import type { BlogArticleWithMeta, BlogBlock } from './types';
import '../website.css';

const formatDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

function ArticleCard({ article }: { article: BlogArticleWithMeta }) {
  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group flex h-full flex-col rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.05] to-transparent p-6 transition-all hover:-translate-y-1 hover:border-violet-400/40"
    >
      <span className="w-fit rounded-full bg-violet-500/15 px-3 py-1 text-xs font-semibold text-violet-200">
        {article.category}
      </span>
      <h2 className="mt-4 text-lg font-semibold leading-snug text-white group-hover:text-violet-100">
        {article.title}
      </h2>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{article.excerpt}</p>
      <p className="mt-5 flex items-center justify-between text-xs text-slate-400">
        <span className="flex items-center gap-1.5">
          <Clock size={13} aria-hidden /> {article.readingMinutes} min de leitura
        </span>
        <ArrowRight
          size={16}
          className="text-slate-400 transition-all group-hover:translate-x-1 group-hover:text-violet-300"
          aria-hidden
        />
      </p>
    </Link>
  );
}

export function PageShell({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { platform, trialDays } = usePlatformData();
  useHashScroll();
  return (
    <div className="vw-root min-h-screen">
      <a
        href="#conteudo"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-slate-900"
      >
        Pular para o conteúdo
      </a>
      <SiteHeader isAuthenticated={isAuthenticated} basePath="/" />
      <main id="conteudo" className="relative isolate">
        <div className="vw-grid absolute inset-x-0 top-0 -z-10 h-[520px]" aria-hidden />
        <div
          className="vw-orb -z-10 left-[-10%] top-[-10%] h-[380px] w-[380px] bg-violet-700"
          aria-hidden
        />
        {children}
      </main>
      <SiteFooter platform={platform} basePath="/" />
      <WhatsAppFloat number={platform?.supportWhatsApp} />
      <MobileStickyCta label={`Testar grátis por ${trialDays} dias`} />
    </div>
  );
}

export function BlogIndexPage() {
  const [category, setCategory] = useState<string>('Todos');
  usePageMeta(BLOG_TITLE, BLOG_DESCRIPTION, '/blog');
  const list =
    category === 'Todos' ? BLOG_ARTICLES : BLOG_ARTICLES.filter(a => a.category === category);

  return (
    <PageShell>
      <section className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-6 sm:pt-36 lg:px-8">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-violet-400/25 bg-violet-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-violet-300">
            Blog
          </span>
          <h1 className="mt-4 text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
            Delivery, vendas e gestão <span className="vw-gradient-text">sem complicação</span>
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-300">
            Guias práticos para vender mais pelo seu próprio canal, atender melhor no WhatsApp e
            organizar o seu negócio.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2" role="group" aria-label="Filtrar por categoria">
          {['Todos', ...BLOG_CATEGORIES].map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              aria-pressed={category === c}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                category === c
                  ? 'bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white'
                  : 'border border-white/10 bg-white/[0.03] text-slate-300 hover:text-white'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {list.map(article => (
            <li key={article.slug}>
              <ArticleCard article={article} />
            </li>
          ))}
        </ul>
      </section>
    </PageShell>
  );
}

export function Block({ block }: { block: BlogBlock }) {
  switch (block.type) {
    case 'h2':
      return (
        <h2 className="mt-12 text-2xl font-bold tracking-tight text-white sm:text-3xl">
          {block.text}
        </h2>
      );
    case 'h3':
      return <h3 className="mt-8 text-xl font-semibold text-white">{block.text}</h3>;
    case 'ul':
      return (
        <ul className="mt-5 list-disc space-y-2 pl-6 marker:text-violet-400">
          {block.items.map(item => (
            <li key={item}>
              <RichText text={item} />
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className="mt-5 list-decimal space-y-2 pl-6 marker:font-semibold marker:text-violet-300">
          {block.items.map(item => (
            <li key={item}>
              <RichText text={item} />
            </li>
          ))}
        </ol>
      );
    case 'tip':
      return (
        <aside className="mt-8 flex gap-3 rounded-2xl border border-violet-400/25 bg-violet-500/10 p-5">
          <Lightbulb size={20} className="mt-0.5 shrink-0 text-violet-300" aria-hidden />
          <div>
            {block.title && <p className="font-semibold text-white">{block.title}</p>}
            <p className="mt-1">
              <RichText text={block.text} />
            </p>
          </div>
        </aside>
      );
    case 'table':
      return (
        <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full min-w-[520px] text-left text-sm">
            <thead className="bg-white/[0.05] text-white">
              <tr>
                {block.head.map(h => (
                  <th key={h} scope="col" className="px-4 py-3 font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {block.rows.map(row => (
                <tr key={row.join('|')}>
                  {row.map((cell, i) => (
                    <td key={i} className={`px-4 py-3 ${i === 0 ? 'font-medium text-white' : ''}`}>
                      <RichText text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'cta': {
      const href = block.segment ? `/cadastro?segmento=${block.segment}` : '/cadastro';
      return (
        <div className="vw-gradient-border mt-12 rounded-3xl bg-gradient-to-br from-violet-950/80 to-slate-900/80 p-7 text-center">
          <p className="text-lg font-semibold text-white">{block.text}</p>
          <Link
            to={href}
            className="group mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-3.5 font-semibold text-slate-900 transition hover:bg-violet-100"
          >
            Criar minha loja grátis
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      );
    }
    default:
      return (
        <p className="mt-5">
          <RichText text={block.text} />
        </p>
      );
  }
}

export function BlogArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = findArticle(slug);
  if (!article) return <Navigate to="/blog" replace />;
  return <ArticleContent key={article.slug} article={article} />;
}

function ArticleContent({ article }: { article: BlogArticleWithMeta }) {
  usePageMeta(
    `${article.title} | ${BRAND}`,
    article.description,
    `/blog/${article.slug}`,
    'article',
  );
  const segment = article.relatedSegment ? findSegmentBySlug(article.relatedSegment) : undefined;
  const related = relatedArticles(article);

  return (
    <PageShell>
      <article className="mx-auto max-w-3xl px-4 pb-10 pt-28 sm:px-6 sm:pt-36 lg:px-8">
        <nav aria-label="Trilha" className="text-sm text-slate-400">
          <Link to="/blog" className="inline-flex items-center gap-1.5 hover:text-white">
            <ArrowLeft size={15} aria-hidden /> Blog
          </Link>
          <span className="mx-2 text-slate-600">/</span>
          <span>{article.category}</span>
        </nav>
        <h1 className="mt-5 text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
          {article.title}
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-slate-300">{article.excerpt}</p>
        <p className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-400">
          <span>Por Equipe {BRAND}</span>
          <span className="flex items-center gap-1.5">
            <CalendarDays size={14} aria-hidden />
            <time dateTime={article.updatedAt}>{formatDate(article.updatedAt)}</time>
          </span>
          <span className="flex items-center gap-1.5">
            <Clock size={14} aria-hidden /> {article.readingMinutes} min de leitura
          </span>
        </p>

        <div className="mt-8 border-t border-white/10 pt-2 text-base leading-relaxed text-slate-300 sm:text-lg">
          {article.blocks.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </div>

        {segment && (
          <Link
            to={`/para/${segment.slug}`}
            className="group mt-10 flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition hover:border-violet-400/40"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-violet-500/15 text-violet-200">
              <segment.icon size={22} aria-hidden />
            </span>
            <span className="flex-1">
              <span className="block font-semibold text-white">
                {BRAND} para {segment.audience}
              </span>
              <span className="mt-0.5 block text-sm text-slate-400">{segment.description}</span>
            </span>
            <ArrowRight
              size={18}
              className="text-slate-400 transition group-hover:translate-x-1 group-hover:text-violet-300"
              aria-hidden
            />
          </Link>
        )}
      </article>

      {article.faq.length > 0 && <Faq items={article.faq} title="Perguntas sobre este assunto" />}

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-white">Continue lendo</h2>
        <ul className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map(a => (
            <li key={a.slug}>
              <ArticleCard article={a} />
            </li>
          ))}
        </ul>
      </section>
    </PageShell>
  );
}
